import type { BrandId, MultiBrandConfig } from './types';
import { esimsupra } from './esimsupra';
import { esimcobra } from './esimcobra';
import { skylane } from './skylane';
import { aquaglobe } from './aquaglobe';
import { wanderlite } from './wanderlite';
import { photoCommerce } from './photo-commerce';
import { redigitalNeon } from './redigital-neon-dark-premium';
import { coastalFreedom } from './coastal-freedom-travel-esim';
import { esimIndex } from './esim-index-comparison-engine';
import { travelBlog } from './travel-destination-blog-affiliate-engine';

export * from './types';

/**
 * Registry of implemented brands. All ten portfolio brands (sites 01–10) are
 * now registered below. Unknown IDs fall back only in non-production tooling;
 * production builds fail closed rather than publishing the default brand.
 */
export const brands: Partial<Record<BrandId, MultiBrandConfig>> = {
  esimsupra,
  'bold-fast-activation': esimcobra,
  'airport-fast-lane': skylane,
  'aquaglass-global-connect': aquaglobe,
  'wanderlite-explorer': wanderlite,
  'photo-commerce-travel-connected': photoCommerce,
  'redigital-neon-dark-premium': redigitalNeon,
  'coastal-freedom-travel-esim': coastalFreedom,
  'esim-index-comparison-engine': esimIndex,
  'travel-destination-blog-affiliate-engine': travelBlog,
};

export const DEFAULT_BRAND_ID: BrandId = 'esimsupra';

/**
 * Resolve the active brand id at build time.
 *
 * Precedence: NEXT_PUBLIC_BRAND_ID (inlined into the client bundle by Next)
 * → BRAND_ID (build scripts / server) → eSIMSUPRA default. Keeping the default
 * means an unset env reproduces the original single-brand behavior exactly.
 */
export function getActiveBrandId(): BrandId {
  const publicRaw = (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_BRAND_ID) || '';
  const privateRaw = (typeof process !== 'undefined' && process.env.BRAND_ID) || '';
  const isProduction = typeof process !== 'undefined' && process.env.NODE_ENV === 'production';
  if (publicRaw.trim() && privateRaw.trim() && publicRaw.trim() !== privateRaw.trim()) {
    const message = `[brands] NEXT_PUBLIC_BRAND_ID "${publicRaw}" conflicts with BRAND_ID "${privateRaw}".`;
    if (isProduction) throw new Error(message);
    // eslint-disable-next-line no-console
    console.warn(message);
  }
  const raw = publicRaw || privateRaw;
  const id = raw.trim() as BrandId;
  if (id && brands[id]) return id;
  if (id && !brands[id]) {
    const message = `[brands] BRAND_ID "${id}" is not registered.`;
    if (isProduction) throw new Error(message);
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.warn(`${message} Falling back to "${DEFAULT_BRAND_ID}".`);
    }
    return DEFAULT_BRAND_ID;
  }
  return DEFAULT_BRAND_ID;
}

/** The resolved active brand config for this build. */
export function getActiveBrand(): MultiBrandConfig {
  return brands[getActiveBrandId()] ?? esimsupra;
}

/**
 * Rewrite the default brand's display name ("eSIMSupra") to the ACTIVE brand's
 * display name in user-facing content. No-op when the active brand IS the
 * default, so eSIMSUPRA output is unchanged. Case-sensitive on the branded
 * token only, so it never touches the lowercase domain (e.g. esimsupra.com).
 */
export function normalizeBrandText(text: string | undefined): string | undefined {
  if (!text) return text;
  const activeBrand = getActiveBrand().legacy;
  const active = activeBrand.brand.displayName;
  const def = esimsupra.legacy.brand.displayName; // "eSIMSupra"
  if (active === def) return text;
  return text
    .split(def).join(active)
    .split('eSIM Supra').join(active)
    .split(esimsupra.legacy.contact.email).join(activeBrand.contact.email)
    .split(esimsupra.legacy.website.domain).join(activeBrand.website.domain);
}

export { esimsupra, esimcobra, skylane, aquaglobe, wanderlite, photoCommerce, redigitalNeon, coastalFreedom, esimIndex, travelBlog };
